import { prepareHeaders } from '@evolve/common-ui';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

const analyticsBaseUrl = () =>
  process.env.GATSBY_ANALYTICS_API_URL ??
  (process.env.GATSBY_API_URL
    ? `${process.env.GATSBY_API_URL}/analytics`
    : '');

export type TAnadoluAnalyticsInitiativeRow = {
  id: string;
  initiativeDate?: string;
  comments?: string;
  initiativeType?: { id?: string; name?: string };
  user?: { id?: string; acronym?: string; name?: string };
  agency?: { id?: string; name?: string };
  contact?: { id?: string; name?: string };
};

export type AnadoluAnalyticsInitiativesListFilters = {
  initiativeDateFrom?: string;
  initiativeDateTo?: string;
  initiativeTypeIds?: string;
  userIds?: string;
  agencyIds?: string;
  contactIds?: string;
};

export type GetAnadoluAnalyticsInitiativesArgs = {
  customerId: string;
  pageOffset?: number;
  pageLimit?: number;
  populate?: string;
  filters?: AnadoluAnalyticsInitiativesListFilters;
  sort?: string[];
};

export type TAnadoluInitiativeType = {
  id: string;
  name?: string;
  acronym?: string;
  active?: boolean;
};

type JsonApiResource = {
  type: string;
  id: string;
  attributes?: Record<string, unknown>;
  relationships?: Record<
    string,
    { data?: { type: string; id: string } | null }
  >;
};

function resourceToObject(res: JsonApiResource): Record<string, unknown> {
  return {
    id: res.id,
    ...(res.attributes ?? {}),
  };
}

function mergeJsonApiAnadoluInitiatives(response: unknown): {
  values: TAnadoluAnalyticsInitiativeRow[];
  total: number;
  ids: string[];
} {
  const body = response as {
    data?: JsonApiResource | JsonApiResource[];
    included?: JsonApiResource[];
    meta?: { total?: number; ids?: string[] };
  };
  const included = body.included ?? [];
  const byKey = new Map(
    included.map((r) => [`${r.type}:${r.id}`, r] as const),
  );

  const resolveRel = (rel: { type: string; id: string } | null | undefined) => {
    if (!rel?.type || !rel.id) return undefined;
    const inc = byKey.get(`${rel.type}:${rel.id}`);
    if (!inc) return { id: rel.id, type: rel.type };
    return resourceToObject(inc) as TAnadoluAnalyticsInitiativeRow['user'];
  };

  const rawRows = Array.isArray(body.data)
    ? body.data
    : body.data
      ? [body.data]
      : [];

  const values: TAnadoluAnalyticsInitiativeRow[] = rawRows.map((row) => {
    const base = resourceToObject(row) as unknown as TAnadoluAnalyticsInitiativeRow;
    const rels = row.relationships ?? {};
    if (rels.user?.data) {
      base.user = resolveRel(rels.user.data);
    }
    if (rels.agency?.data) {
      base.agency = resolveRel(rels.agency.data) as TAnadoluAnalyticsInitiativeRow['agency'];
    }
    if (rels.contact?.data) {
      base.contact = resolveRel(rels.contact.data) as TAnadoluAnalyticsInitiativeRow['contact'];
    }
    if (rels.initiativeType?.data) {
      base.initiativeType = resolveRel(
        rels.initiativeType.data,
      ) as TAnadoluAnalyticsInitiativeRow['initiativeType'];
    }
    return base;
  });

  return {
    values,
    total: body.meta?.total ?? values.length,
    ids: (body.meta?.ids ?? []).map(String),
  };
}

function appendAnadoluInitiativeFilters(
  params: URLSearchParams,
  filters?: AnadoluAnalyticsInitiativesListFilters,
) {
  if (!filters) return;

  const { initiativeDateFrom, initiativeDateTo, ...rest } = filters;

  if (initiativeDateFrom?.trim() || initiativeDateTo?.trim()) {
    const from =
      initiativeDateFrom?.trim() || initiativeDateTo?.trim() || '';
    const to = initiativeDateTo?.trim() || initiativeDateFrom?.trim() || '';
    if (from) {
      params.set('filter[initiativeDate]', `${from},${to}`);
    }
  }

  if (rest.initiativeTypeIds?.trim()) {
    params.set('filter[initiativeTypeId]', rest.initiativeTypeIds.trim());
  }
  if (rest.userIds?.trim()) {
    params.set('filter[userId]', rest.userIds.trim());
  }
  if (rest.agencyIds?.trim()) {
    params.set('filter[agencyId]', rest.agencyIds.trim());
  }
  if (rest.contactIds?.trim()) {
    params.set('filter[contactId]', rest.contactIds.trim());
  }
}

export const analyticsAnadoluInitiativesApi = createApi({
  reducerPath: 'analyticsAnadoluInitiatives',
  baseQuery: fetchBaseQuery({
    baseUrl: analyticsBaseUrl(),
    mode: 'cors',
    prepareHeaders,
  }),
  tagTypes: ['AnadoluAnalyticsInitiative', 'AnadoluInitiativeType'],
  endpoints: (builder) => ({
    getAnadoluAnalyticsInitiatives: builder.query<
      { values: TAnadoluAnalyticsInitiativeRow[]; total: number; ids: string[] },
      GetAnadoluAnalyticsInitiativesArgs
    >({
      providesTags: ['AnadoluAnalyticsInitiative'],
      query: ({
        customerId,
        pageOffset = 0,
        pageLimit = 10,
        populate = 'user,agency,contact,initiativeType',
        filters,
        sort,
      }) => {
        const params = new URLSearchParams();
        params.set('page[offset]', String(pageOffset));
        params.set('page[limit]', String(pageLimit));
        params.set('filter[customerId]', String(customerId));
        appendAnadoluInitiativeFilters(params, filters);
        if (populate) {
          params.set('populate', populate);
        }
        if (sort?.length) {
          params.set('sort', sort.join(','));
        }
        return {
          url: `anadolu/initiatives?${params.toString()}`,
        };
      },
      transformResponse: mergeJsonApiAnadoluInitiatives,
    }),
    getAnadoluInitiativeTypes: builder.query<
      { values: TAnadoluInitiativeType[]; total: number },
      void
    >({
      providesTags: ['AnadoluInitiativeType'],
      query: () => ({ url: 'anadolu/initiatives/types' }),
      transformResponse: (response: unknown) => {
        const body = response as {
          data?: JsonApiResource | JsonApiResource[];
          meta?: { total?: number };
        };
        const rows = Array.isArray(body.data)
          ? body.data
          : body.data
            ? [body.data]
            : [];
        const values = rows.map((row) => ({
          id: String(row.id),
          ...(row.attributes as Omit<TAnadoluInitiativeType, 'id'>),
        }));
        return {
          values,
          total: body.meta?.total ?? values.length,
        };
      },
    }),
    deleteAnadoluAnalyticsInitiative: builder.mutation<void, { id: string }>({
      invalidatesTags: ['AnadoluAnalyticsInitiative'],
      query: ({ id }) => ({
        url: `anadolu/initiatives/${id}`,
        method: 'DELETE',
      }),
    }),
  }),
});

export const {
  useGetAnadoluAnalyticsInitiativesQuery,
  useGetAnadoluInitiativeTypesQuery,
  useDeleteAnadoluAnalyticsInitiativeMutation,
} = analyticsAnadoluInitiativesApi;
